import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import useSignout from "../hooks/useSignout";
import useZammadForm from "../hooks/useZammadForm";
import useAuthSnapshot from "../hooks/useAuthSnapshot";
import { useOperatorMessageSettings } from "../hooks/useOperators";
import { clearStoredAuth, setStoredAuthUser } from "../utils";
import { MessagePanel, getLastSeenReceivedAt } from "./messages";
import { useOpsMessages } from "../hooks/useOpsMessages";
import { prefetchDefaultOpsOverview } from "../utils/opsOverview";
import { useThemeMode } from "../theme";
import { buildNavigationItems, getRoutePath } from "../routes/catalog";
import type { NavigationPolicy } from "../routes/catalog";
import { AppNavigationShell } from "./AppNavigationShell";
import { useNavigationSummary } from "../hooks/useNavigationSummary";

type NavItemId = NavigationPolicy;

type NavItem = {
  id: NavItemId;
  label: string;
  to: string;
  isVisible: boolean;
};

type ViewAsOperatorOption = {
  id: number;
  name: string | null;
  company: string | null;
  icao_code: string | null;
  is_dashboard_enabled: boolean;
};

const parseErrorMessage = async (res: Response) => {
  try {
    const payload = await res.json();
    if (payload?.message && typeof payload.message === "string") {
      return payload.message;
    }
  } catch (_error) {
    // Fall back to HTTP status text.
  }

  return res.statusText || `HTTP ${res.status}`;
};

export const buildViewAsButtonLabel = ({
  isViewAsActive,
  viewAsOperatorName,
  operatorRef,
}: {
  isViewAsActive: boolean;
  viewAsOperatorName: string | null;
  operatorRef: number | null;
}) => {
  if (!isViewAsActive) {
    return "View as";
  }

  if (viewAsOperatorName) {
    return `View as: ${viewAsOperatorName}`;
  }

  if (operatorRef) {
    return `View as: #${operatorRef}`;
  }

  return "View as: active";
};

export const isViewAsSwitchDisabled = ({
  isViewAsSubmitting,
  isViewAsLoading,
  selectedOperatorId,
  currentOperatorRef,
  isViewAsActive,
}: {
  isViewAsSubmitting: boolean;
  isViewAsLoading: boolean;
  selectedOperatorId: number | null;
  currentOperatorRef: number | null;
  isViewAsActive: boolean;
}) =>
  isViewAsSubmitting ||
  isViewAsLoading ||
  !selectedOperatorId ||
  (isViewAsActive && selectedOperatorId === currentOperatorRef);

export const buildNavItems = ({
  hasFuelAccess,
  hasReportsAccess,
  hasTransferAccess,
  hasJourneyLogAccess = false,
  isDashboardEnabled,
  isAcmiClient = false,
  isRefuelEuReader = false,
  showLibraryLink,
  showAdminLink,
}: {
  hasFuelAccess: boolean;
  hasReportsAccess: boolean;
  hasTransferAccess: boolean;
  hasJourneyLogAccess?: boolean;
  isDashboardEnabled: boolean;
  isAcmiClient?: boolean;
  isRefuelEuReader?: boolean;
  showLibraryLink: boolean;
  showAdminLink: boolean;
}): NavItem[] =>
  buildNavigationItems({
    hasFuelAccess,
    hasReportsAccess,
    hasTransferAccess,
    hasJourneyLogAccess,
    isDashboardEnabled,
    isAcmiClient,
    isRefuelEuReader,
    showLibraryLink,
    showAdminLink,
  });

export const Navigation: FunctionComponent = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mode: themeMode, toggleMode: toggleThemeMode } = useThemeMode();
  const {
    userData,
    isAuthenticated,
    isSuperAdmin,
    isAcmiClient,
    isRefuelEuReader,
    canManageUsers,
    bookedOptions,
    hasJourneyLogAccess,
    operatorRef,
    canUseViewAs,
    isViewAsActive,
    viewAsOperatorName,
    superAdminContextState,
    isSuperAdminContextActive,
    superAdminContextOperatorName,
  } = useAuthSnapshot();
  const email = userData?.email;
  const isDashboardEnabled = Boolean(userData?.is_dashboard_enabled);
  const showAdminLink = canManageUsers && !isAcmiClient;

  const reportOptionIds = useMemo(
    () =>
      new Set([
        "reports_fuel",
        "reports_approach",
        "reports_otp_turnaround",
        "reports_crew_duty",
      ]),
    [],
  );
  const hasFuelAccess =
    isSuperAdmin ||
    bookedOptions.includes("refuel_eu") ||
    bookedOptions.includes("fuel_assurance");
  const hasTransferAccess =
    isSuperAdmin || bookedOptions.includes("leon_transfer_check");
  const hasReportsAccess = useMemo(
    () =>
      isSuperAdmin ||
      bookedOptions.some((option: string) => reportOptionIds.has(option)),
    [bookedOptions, isSuperAdmin, reportOptionIds],
  );

  const { handleSignout } = useSignout();

  const [isMessagePanelOpen, setIsMessagePanelOpen] = useState(false);

  const [isViewAsDialogOpen, setIsViewAsDialogOpen] = useState(false);
  const [viewAsOperators, setViewAsOperators] = useState<
    ViewAsOperatorOption[]
  >([]);
  const [isViewAsLoading, setIsViewAsLoading] = useState(false);
  const [isViewAsSubmitting, setIsViewAsSubmitting] = useState(false);
  const [viewAsError, setViewAsError] = useState("");
  const [selectedViewAsOperator, setSelectedViewAsOperator] =
    useState<ViewAsOperatorOption | null>(null);

  const handleUnauthorized = useCallback(() => {
    clearStoredAuth();
    queryClient.clear();
    navigate(getRoutePath("home"));
  }, [navigate, queryClient]);

  const applyAuthPayload = useCallback(
    (payload: any) => {
      setStoredAuthUser(payload);
      queryClient.clear();
      const isPayloadAcmiClient = payload?.role === "acmi_client";
      const isPayloadRefuelEuReader = payload?.role === "refuel_eu_reader";
      setIsViewAsDialogOpen(false);
      setViewAsError("");
      setSelectedViewAsOperator(null);
      navigate(isPayloadRefuelEuReader ? getRoutePath("fuel-refuel-eu") : isPayloadAcmiClient ? getRoutePath("flights") : getRoutePath("ops"));
      if (!isPayloadAcmiClient && !isPayloadRefuelEuReader) {
        void prefetchDefaultOpsOverview({
          queryClient,
          operatorId:
            Number.parseInt(`${payload?.operator_ref ?? ""}`, 10) || null,
        }).catch(() => {
          // Best-effort prefetch only.
        });
      }
    },
    [navigate, queryClient],
  );

  const handleNavIntent = useCallback(
    (itemId: NavItemId) => {
      if (isAcmiClient || isRefuelEuReader || itemId !== "ops") {
        return;
      }

      prefetchDefaultOpsOverview({
        queryClient,
        operatorId: operatorRef,
      }).catch(() => {
        // Best-effort prefetch only.
      });
    },
    [isAcmiClient, isRefuelEuReader, operatorRef, queryClient],
  );

  const handleOpenViewAsDialog = useCallback(() => {
    if (!canUseViewAs) {
      return;
    }

    setViewAsError("");
    setIsViewAsDialogOpen(true);
  }, [canUseViewAs]);

  const handleCloseViewAsDialog = useCallback(() => {
    if (isViewAsSubmitting) {
      return;
    }

    setIsViewAsDialogOpen(false);
    setViewAsError("");
  }, [isViewAsSubmitting]);

  useEffect(() => {
    if (!isViewAsDialogOpen || !canUseViewAs) {
      return;
    }

    let isCancelled = false;

    const loadOperators = async () => {
      setIsViewAsLoading(true);
      setViewAsError("");

      try {
        const res = await fetch(`${API_URL}/auth/view-as/operators`, {
          credentials: "include",
        });

        if (res.status === 401) {
          if (!isCancelled) {
            handleUnauthorized();
          }
          return;
        }

        if (!res.ok) {
          throw new Error(await parseErrorMessage(res));
        }

        const payload = await res.json();
        if (!isCancelled) {
          setViewAsOperators(
            Array.isArray(payload?.operators) ? payload.operators : [],
          );
        }
      } catch (error) {
        if (isCancelled) {
          return;
        }

        setViewAsError(
          error instanceof Error && error.message
            ? error.message
            : "Unable to load operators for view-as mode.",
        );
      } finally {
        if (!isCancelled) {
          setIsViewAsLoading(false);
        }
      }
    };

    loadOperators();

    return () => {
      isCancelled = true;
    };
  }, [canUseViewAs, handleUnauthorized, isViewAsDialogOpen]);

  useEffect(() => {
    if (!isAuthenticated || isAcmiClient || isRefuelEuReader) {
      return;
    }

    prefetchDefaultOpsOverview({
      queryClient,
      operatorId: operatorRef,
    }).catch(() => {
      // Best-effort prefetch only.
    });
  }, [isAcmiClient, isRefuelEuReader, isAuthenticated, operatorRef, queryClient]);

  useEffect(() => {
    if (!isViewAsDialogOpen) {
      return;
    }

    if (!viewAsOperators.length) {
      setSelectedViewAsOperator(null);
      return;
    }

    const parsedContextOperatorRef = Number.parseInt(
      `${superAdminContextState?.operator_ref ?? ""}`,
      10,
    );
    const contextOperatorRef = Number.isFinite(parsedContextOperatorRef)
      ? parsedContextOperatorRef
      : null;

    const parsedOriginalOperatorRef = Number.parseInt(
      `${superAdminContextState?.original_operator_ref ?? ""}`,
      10,
    );
    const originalOperatorRef = Number.isFinite(parsedOriginalOperatorRef)
      ? parsedOriginalOperatorRef
      : null;

    const preferredOperatorRef =
      isViewAsActive && isSuperAdmin
        ? contextOperatorRef || originalOperatorRef
        : operatorRef;

    const activeOperator =
      viewAsOperators.find(
        (operator) => operator.id === preferredOperatorRef,
      ) || null;
    setSelectedViewAsOperator(activeOperator);
  }, [
    isSuperAdmin,
    isViewAsActive,
    isViewAsDialogOpen,
    operatorRef,
    superAdminContextState?.operator_ref,
    superAdminContextState?.original_operator_ref,
    viewAsOperators,
  ]);

  const handleActivateViewAs = useCallback(async () => {
    if (!selectedViewAsOperator) {
      return;
    }

    setIsViewAsSubmitting(true);
    setViewAsError("");

    try {
      const res = await fetch(`${API_URL}/auth/view-as`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ operatorId: selectedViewAsOperator.id }),
      });

      if (res.status === 401) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        throw new Error(await parseErrorMessage(res));
      }

      const payload = await res.json();
      applyAuthPayload(payload);
    } catch (error) {
      setViewAsError(
        error instanceof Error && error.message
          ? error.message
          : "Unable to switch view-as context.",
      );
    } finally {
      setIsViewAsSubmitting(false);
    }
  }, [applyAuthPayload, handleUnauthorized, selectedViewAsOperator]);

  const handleDeactivateViewAs = useCallback(async () => {
    setIsViewAsSubmitting(true);
    setViewAsError("");

    try {
      const res = await fetch(`${API_URL}/auth/view-as`, {
        method: "DELETE",
        credentials: "include",
      });

      if (res.status === 401) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        throw new Error(await parseErrorMessage(res));
      }

      const payload = await res.json();
      applyAuthPayload(payload);
    } catch (error) {
      setViewAsError(
        error instanceof Error && error.message
          ? error.message
          : "Unable to deactivate view-as mode.",
      );
    } finally {
      setIsViewAsSubmitting(false);
    }
  }, [applyAuthPayload, handleUnauthorized]);

  const handleSetSuperAdminContext = useCallback(async () => {
    if (!selectedViewAsOperator) {
      return;
    }

    setIsViewAsSubmitting(true);
    setViewAsError("");

    try {
      const res = await fetch(`${API_URL}/auth/super-admin-context`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ operatorId: selectedViewAsOperator.id }),
      });

      if (res.status === 401) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        throw new Error(await parseErrorMessage(res));
      }

      const payload = await res.json();
      applyAuthPayload(payload);
    } catch (error) {
      setViewAsError(
        error instanceof Error && error.message
          ? error.message
          : "Unable to set super-admin operator context.",
      );
    } finally {
      setIsViewAsSubmitting(false);
    }
  }, [applyAuthPayload, handleUnauthorized, selectedViewAsOperator]);

  const handleClearSuperAdminContext = useCallback(async () => {
    setIsViewAsSubmitting(true);
    setViewAsError("");

    try {
      const res = await fetch(`${API_URL}/auth/super-admin-context`, {
        method: "DELETE",
        credentials: "include",
      });

      if (res.status === 401) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        throw new Error(await parseErrorMessage(res));
      }

      const payload = await res.json();
      applyAuthPayload(payload);
    } catch (error) {
      setViewAsError(
        error instanceof Error && error.message
          ? error.message
          : "Unable to reset super-admin operator context.",
      );
    } finally {
      setIsViewAsSubmitting(false);
    }
  }, [applyAuthPayload, handleUnauthorized]);

  const parsedContextOperatorRef = Number.parseInt(
    `${superAdminContextState?.operator_ref ?? ""}`,
    10,
  );
  const contextOperatorRef = Number.isFinite(parsedContextOperatorRef)
    ? parsedContextOperatorRef
    : null;
  const parsedOriginalSuperAdminOperatorRef = Number.parseInt(
    `${superAdminContextState?.original_operator_ref ?? ""}`,
    10,
  );
  const originalSuperAdminOperatorRef = Number.isFinite(
    parsedOriginalSuperAdminOperatorRef,
  )
    ? parsedOriginalSuperAdminOperatorRef
    : null;
  const effectiveSuperAdminOperatorRef =
    contextOperatorRef ||
    originalSuperAdminOperatorRef ||
    (isViewAsActive ? null : operatorRef);
  const originalSuperAdminOperatorName =
    typeof superAdminContextState?.original_operator_name === "string" &&
    superAdminContextState.original_operator_name.length
      ? superAdminContextState.original_operator_name
      : null;
  const activeSuperAdminOperatorLabel =
    superAdminContextOperatorName ||
    (contextOperatorRef ? `Operator #${contextOperatorRef}` : "Unknown");
  const defaultSuperAdminOperatorLabel =
    originalSuperAdminOperatorName ||
    (originalSuperAdminOperatorRef
      ? `Operator #${originalSuperAdminOperatorRef}`
      : "Unknown");

  const isSwitchDisabled = isViewAsSwitchDisabled({
    isViewAsSubmitting,
    isViewAsLoading,
    selectedOperatorId: selectedViewAsOperator?.id ?? null,
    currentOperatorRef: operatorRef,
    isViewAsActive,
  });
  const isSetSuperAdminOperatorDisabled =
    isViewAsSubmitting ||
    isViewAsLoading ||
    !selectedViewAsOperator ||
    selectedViewAsOperator.id === effectiveSuperAdminOperatorRef;
  const isResetSuperAdminOperatorDisabled =
    isViewAsSubmitting || !isSuperAdminContextActive;

  const viewAsButtonLabel = useMemo(
    () =>
      buildViewAsButtonLabel({
        isViewAsActive,
        viewAsOperatorName,
        operatorRef,
      }),
    [isViewAsActive, operatorRef, viewAsOperatorName],
  );

  // Get operator's message settings
  const { data: messageSettings } = useOperatorMessageSettings(
    isAuthenticated && !isAcmiClient && !isRefuelEuReader && operatorRef ? operatorRef : null,
  );
  const isMessagingEnabled = messageSettings?.messaging_enabled || false;
  const { data: opsMessageData, isLoading: isMessagesLoading } = useOpsMessages(
    isAuthenticated && !isAcmiClient && !isRefuelEuReader && isMessagingEnabled,
  );
  const messageFlights = opsMessageData?.flights ?? [];
  const { data: navigationSummary } = useNavigationSummary(
    isAuthenticated && !isAcmiClient && !isRefuelEuReader,
  );

  const [lastSeenVersion, setLastSeenVersion] = useState(0);
  const unreadMessageCount = useMemo(() => {
    return messageFlights.filter((flight) => {
      const latestReceived = flight.noteStatus?.latestReceivedAt;
      if (!latestReceived) {
        return false;
      }
      const lastSeen = getLastSeenReceivedAt(flight.flightID);
      if (!lastSeen) {
        return true;
      }

      return new Date(latestReceived) > new Date(lastSeen);
    }).length;
  }, [messageFlights, lastSeenVersion]);

  const handleOpenMessagePanel = useCallback(() => {
    setIsMessagePanelOpen(true);
  }, []);

  const handleCloseMessagePanel = useCallback(() => {
    setIsMessagePanelOpen(false);
  }, []);
  const handleSeenUpdate = useCallback(() => {
    setLastSeenVersion((value) => value + 1);
  }, []);

  useZammadForm(email, !isAcmiClient && !isRefuelEuReader);

  const navItems = useMemo<NavItem[]>(
    () =>
      buildNavItems({
        hasFuelAccess,
        hasReportsAccess,
        hasTransferAccess,
        hasJourneyLogAccess,
        isDashboardEnabled,
        isAcmiClient,
        isRefuelEuReader,
        showLibraryLink: canManageUsers,
        showAdminLink,
      }),
    [
      hasFuelAccess,
      hasReportsAccess,
      hasTransferAccess,
      hasJourneyLogAccess,
      isAcmiClient,
      isRefuelEuReader,
      isDashboardEnabled,
      canManageUsers,
      showAdminLink,
    ],
  );

  const visibleRouteNavItems = useMemo(
    () => navItems.filter((item) => item.isVisible),
    [navItems],
  );
  useEffect(() => {
    if (!isAuthenticated) {
      setIsViewAsDialogOpen(false);
    }
  }, [isAuthenticated]);

  return (
    <>
      <AppNavigationShell
        isAuthenticated={isAuthenticated}
        isAcmiClient={isAcmiClient}
        isRefuelEuReader={isRefuelEuReader}
        navItems={visibleRouteNavItems}
        email={typeof email === "string" ? email : undefined}
        operatorName={
          viewAsOperatorName ||
          (typeof userData?.operator_name === "string" ? userData.operator_name : undefined)
        }
        roleLabel={isRefuelEuReader ? "RefuelEU" : typeof userData?.role === "string" ? userData.role.replace(/_/g, " ") : undefined}
        canUseViewAs={canUseViewAs}
        isViewAsActive={isViewAsActive}
        viewAsButtonLabel={viewAsButtonLabel}
        themeMode={themeMode}
        isMessagingEnabled={isMessagingEnabled}
        unreadMessageCount={unreadMessageCount}
        navigationBadges={{
          transfer: navigationSummary?.transferPendingCount ?? null,
        }}
        onNavIntent={handleNavIntent}
        onOpenViewAs={handleOpenViewAsDialog}
        onOpenMessages={handleOpenMessagePanel}
        onToggleTheme={toggleThemeMode}
        onSignout={handleSignout}
      />

      <Dialog
        open={isViewAsDialogOpen}
        onClose={handleCloseViewAsDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>View As / Super Admin Context</DialogTitle>
        <DialogContent>
          {isViewAsActive && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Active context:{" "}
              {viewAsOperatorName ||
                (operatorRef ? `Operator #${operatorRef}` : "Unknown")}
            </Alert>
          )}
          {isSuperAdmin && (
            <Alert
              severity={isSuperAdminContextActive ? "warning" : "info"}
              sx={{ mb: 2 }}
            >
              {isSuperAdminContextActive
                ? `Super Admin operator context: ${activeSuperAdminOperatorLabel}`
                : `Default Super Admin operator: ${defaultSuperAdminOperatorLabel}`}
            </Alert>
          )}

          {viewAsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {viewAsError}
            </Alert>
          )}

          {isViewAsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Autocomplete
              options={viewAsOperators}
              value={selectedViewAsOperator}
              onChange={(_event, value) => setSelectedViewAsOperator(value)}
              getOptionLabel={(option) => {
                const parts = [option.name || "Unnamed operator"];
                if (option.company) {
                  parts.push(option.company);
                }
                if (option.icao_code) {
                  parts.push(option.icao_code);
                }
                return parts.join(" • ");
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Operator"
                  placeholder="Select operator"
                  helperText="Use for View-as switch or Super Admin operator assignment."
                />
              )}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseViewAsDialog}
            disabled={isViewAsSubmitting}
          >
            Close
          </Button>
          {isViewAsActive && (
            <Button
              onClick={handleDeactivateViewAs}
              disabled={isViewAsSubmitting}
              color="warning"
            >
              Back to Super Admin
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleActivateViewAs}
            disabled={isSwitchDisabled}
          >
            {isViewAsSubmitting ? "Switching..." : "Switch"}
          </Button>
          {isSuperAdmin && (
            <Button
              variant="outlined"
              onClick={handleSetSuperAdminContext}
              disabled={isSetSuperAdminOperatorDisabled}
            >
              Set Super Admin Operator
            </Button>
          )}
          {isSuperAdmin && (
            <Button
              onClick={handleClearSuperAdminContext}
              disabled={isResetSuperAdminOperatorDisabled}
              color="warning"
            >
              Reset Super Admin Operator
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {isMessagingEnabled && (
        <MessagePanel
          open={isMessagePanelOpen}
          onClose={handleCloseMessagePanel}
          flights={messageFlights}
          isLoading={isMessagesLoading}
          onSeenUpdate={handleSeenUpdate}
        />
      )}
    </>
  );
};
